import React from 'react';
import { Col, Form, Row } from 'react-bootstrap-5';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import { countries } from 'helpers/countries';
import PhoneNumberInput from 'components/ui/phone_number_input';
import PasswordInput from 'components/form/password_input';
import { investorTypeMap } from '../../../helpers/vault_access_helper';
import ErrorsForInput, { getErrorsForInput } from '../errors_for_input';
import { Button } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightFromBracket } from '@fortawesome/pro-solid-svg-icons';
import { PRIMARY_ROLE_INVESTOR_OWNER } from '../../../actions/ui';
import { fetchTranslation } from '../../../../../helpers/i18n';

const emailTakenError = (isRegistration) => {
  const suffix = isRegistration
    ? fetchTranslation('please_login_to_continue')
    : fetchTranslation('login_or_fill_out_form_to_continue');
  return `${fetchTranslation('email_already_exists')} ${suffix}`;
};

export default function GateAccountFields({
  disableForm = false,
  emailTaken,
  errors = [],
  formFields,
  isInvalidProfile = false,
  isRegistration,
  primaryRole,
  onFormFieldChange,
  onInvestorFieldChange
}) {
  const {
    address,
    city,
    companyName,
    countryId,
    email,
    firstName,
    investorTypes,
    jobTitle,
    lastName,
    password,
    phoneNumber,
    state,
    zip
  } = formFields;

  return (
    <React.Fragment>
      {!isInvalidProfile && (
        <Form.Group className="mb-4">
          <Form.Label>{fetchTranslation('email_address')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            isInvalid={getErrorsForInput(errors, fetchTranslation('email_in_errors')).length > 0 || disableForm}
            name="email"
            placeholder={fetchTranslation('enter_email_address')}
            required
            value={email}
            onChange={e => onFormFieldChange('email', e.target.value)}
          />
          <ErrorsForInput errors={errors} errorString={fetchTranslation('email_in_errors')}/>
          {emailTaken && (
            <React.Fragment>
              <ErrorsForInput
                errors={[emailTakenError(isRegistration)]}
                errorString={emailTakenError(isRegistration)}
              />
              <Button
                className="w-100 mt-6 js-login"
              >
                <FontAwesomeIcon icon={faRightFromBracket}/>
                {fetchTranslation('login_with_buildout')}
              </Button>
            </React.Fragment>
          )}
        </Form.Group>
      )}


      {!disableForm && isRegistration && (
        <div className="mb-4">
          <PasswordInput
            inputProps={{ isInvalid: getErrorsForInput(errors, fetchTranslation('password_in_errors')).length > 0 }}
            value={password}
            onChange={value => onFormFieldChange('password', value)}
          />
          <ErrorsForInput errors={errors} errorString={fetchTranslation('password_in_errors')}/>
        </div>
      )}

      {primaryRole === PRIMARY_ROLE_INVESTOR_OWNER && isRegistration && (
        <Form.Group className="mb-4">
          <Form.Label>{fetchTranslation('investor_type')} </Form.Label>
          {Object.keys(investorTypeMap).map(key => (
            <Form.Check
              checked={investorTypes.includes(key)}
              disabled={disableForm}
              id={`investory-type-${key}`}
              key={key}
              label={fetchTranslation(key)}
              name={`investor_type_${key}`}
              type="checkbox"
              value={key}
              onChange={onInvestorFieldChange}
            />
          ))}
        </Form.Group>
      )}

      <Form.Group className="mb-4">
        <Form.Label>{fetchTranslation('first_name')} <span className="text-danger">*</span></Form.Label>
        <Form.Control
          disabled={disableForm}
          isInvalid={getErrorsForInput(errors, fetchTranslation('first_name_in_errors')).length > 0}
          name="first_name"
          placeholder={fetchTranslation('first_name')}
          required
          value={firstName}
          onChange={e => onFormFieldChange('firstName', e.target.value)}
        />
        <ErrorsForInput errors={errors} errorString={fetchTranslation('first_name_in_errors')}/>
      </Form.Group>
      <Form.Group className="mb-4">
        <Form.Label>{fetchTranslation('last_name')} <span className="text-danger">*</span></Form.Label>
        <Form.Control
          disabled={disableForm}
          isInvalid={getErrorsForInput(errors, fetchTranslation('last_name_in_errors')).length > 0}
          name="last_name"
          placeholder={fetchTranslation('last_name')}
          required
          value={lastName}
          onChange={e => onFormFieldChange('lastName', e.target.value)}
        />
        <ErrorsForInput errors={errors} errorString={fetchTranslation('last_name_in_errors')}/>
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>{fetchTranslation('phone_number')} <span className="text-danger">*</span></Form.Label>
        <PhoneNumberInput
          className={
            getErrorsForInput(errors, fetchTranslation('phone_number_in_errors')).length > 0 ? 'is-invalid' : ''
          }
          defaultCountry="US"
          disabled={disableForm}
          name="phone_number"
          placeholder="(000) 000-0000"
          value={phoneNumber}
          onChange={value => onFormFieldChange('phoneNumber', value)}
        />
        <ErrorsForInput errors={errors} errorString={fetchTranslation('phone_number_in_errors')}/>
      </Form.Group>
      <Form.Group className="mb-4">
        <Form.Label>{fetchTranslation('company')} <span className="text-danger">*</span></Form.Label>
        <Form.Control
          disabled={disableForm}
          isInvalid={getErrorsForInput(errors, fetchTranslation('company_in_errors')).length > 0}
          name="company"
          placeholder={fetchTranslation('company_name_placeholder')}
          required
          value={companyName}
          onChange={e => onFormFieldChange('companyName', e.target.value)}
        />
        <ErrorsForInput errors={errors} errorString={fetchTranslation('company_in_errors')}/>
      </Form.Group>
      <Form.Group className="mb-4">
        <Form.Label>{fetchTranslation('job_title')} <span className="text-danger">*</span></Form.Label>
        <Form.Control
          disabled={disableForm}
          isInvalid={getErrorsForInput(errors, fetchTranslation('job_title_in_errors')).length > 0}
          name="job_title"
          placeholder={fetchTranslation('job_title')}
          required
          value={jobTitle}
          onChange={e => onFormFieldChange('jobTitle', e.target.value)}
        />
        <ErrorsForInput errors={errors} errorString={fetchTranslation('job_title_in_errors')}/>
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>{fetchTranslation('country')} <span className="text-danger">*</span></Form.Label>
        <Form.Select
          disabled={disableForm}
          name="country"
          value={countryId}
          onChange={e => onFormFieldChange('countryId', Number(e.target.value))}
        >
          {map(countries, ({ id, label }) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </Form.Select>
      </Form.Group>

      {isRegistration && (
        <React.Fragment>
          {countryId === 1 && (
            <Form.Group className="mb-4">
              <Form.Label>{fetchTranslation('address')}</Form.Label>
              <Form.Control
                disabled={disableForm}
                name="address"
                placeholder={fetchTranslation('address')}
                value={address}
                onChange={e => onFormFieldChange('address', e.target.value)}
              />
            </Form.Group>
          )}
          <Form.Group className="mb-4">
            <Form.Label>{fetchTranslation('city')}</Form.Label>
            <Form.Control
              disabled={disableForm}
              name="city"
              placeholder={fetchTranslation('city')}
              value={city}
              onChange={e => onFormFieldChange('city', e.target.value)}
            />
          </Form.Group>
          {countryId === 1 && (
            <Row>
              <Col>
                <Form.Group className="mb-4">
                  <Form.Label>{fetchTranslation('state')}</Form.Label>
                  <Form.Control
                    disabled={disableForm}
                    name="state"
                    placeholder={fetchTranslation('state')}
                    value={state}
                    onChange={e => onFormFieldChange('state', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-4">
                  <Form.Label>{fetchTranslation('zip')}</Form.Label>
                  <Form.Control
                    disabled={disableForm}
                    name="zip"
                    placeholder={fetchTranslation('zip')}
                    value={zip}
                    onChange={e => onFormFieldChange('zip', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

GateAccountFields.propTypes = {
  disableForm: PropTypes.bool,
  emailTaken: PropTypes.bool.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string),
  formFields: PropTypes.object.isRequired,
  isInvalidProfile: PropTypes.bool,
  isRegistration: PropTypes.bool.isRequired,
  primaryRole: PropTypes.string,
  onFormFieldChange: PropTypes.func.isRequired,
  onInvestorFieldChange: PropTypes.func
};
